var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState } from 'react';
import { AssigneeType } from '@enums/assignee-type.enum';
import { serverErrorMessage } from '@utils/serverErrorMessage';
import { Button, ConfirmDialog, InlineNotification, SectionHeader } from '@ui';
import VisitBrushaway from './VisitBrushaway';
import VisitHiddenInputs from './VisitHiddenInputs';
import VisitsTable from './VisitsTable';
import { createVisit, destroyVisit, updateVisit } from './api';
import { draftFromRow, newDraft, rowFieldsFromValues } from './draft';
import { reloadPage } from './navigation';
import { t, tGeneric } from './translate';
import { useAssigneeOptions } from './useAssigneeOptions';
import { VisitsMode } from './types';
import './VisitsSectionStyles.scss';
function VisitsSection({ mode, visits, subcontractorsEnabled, showPriority, timeZoneOptions, defaults, buildingId, series, endpoints, inspectionId, canEdit, title, getFormDefaults }) {
    var _a, _b;
    const isImmediate = mode === VisitsMode.IMMEDIATE;
    const keyCounter = React.useRef(0);
    const nextKey = () => {
        keyCounter.current += 1;
        return keyCounter.current;
    };
    const [rows, setRows] = useState(() => visits.map((visit) => (Object.assign(Object.assign({}, visit), { key: nextKey() }))));
    const [editor, setEditor] = useState(null);
    const [removingKey, setRemovingKey] = useState(null);
    const [busy, setBusy] = useState(false);
    const [editorError, setEditorError] = useState(null);
    const [removeError, setRemoveError] = useState(null);
    const { technicianOptions, subcontractorOptions } = useAssigneeOptions(buildingId, subcontractorsEnabled);
    const assigneeName = (row) => {
        var _a, _b;
        if (row.techName)
            return row.techName;
        const options = row.assigneeType === AssigneeType.SUBCONTRACTOR ? subcontractorOptions : technicianOptions;
        const id = row.assigneeType === AssigneeType.SUBCONTRACTOR ? row.subcontractorId : row.technicianId;
        if (id === null)
            return t('unassigned');
        return (_b = (_a = options.find((option) => option.value === id)) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : '';
    };
    const seriesFor = (formDefaults) => series && formDefaults.frequencyLabel !== undefined ? { frequencyLabel: formDefaults.frequencyLabel } : series;
    const openAdd = () => {
        var _a;
        const formDefaults = (_a = getFormDefaults === null || getFormDefaults === void 0 ? void 0 : getFormDefaults()) !== null && _a !== void 0 ? _a : {};
        setEditorError(null);
        setEditor({
            draft: newDraft({ defaults, formDefaults, timeZoneOptions }),
            row: null,
            series: seriesFor(formDefaults)
        });
    };
    const openEdit = (key) => {
        var _a;
        const row = rows.find((candidate) => candidate.key === key);
        if (!row)
            return;
        setEditorError(null);
        setEditor({ draft: draftFromRow(row), row, series: seriesFor((_a = getFormDefaults === null || getFormDefaults === void 0 ? void 0 : getFormDefaults()) !== null && _a !== void 0 ? _a : {}) });
    };
    const closeEditor = () => setEditor(null);
    const stage = (values) => {
        var _a;
        const fields = rowFieldsFromValues(values);
        const editing = (_a = editor === null || editor === void 0 ? void 0 : editor.row) !== null && _a !== void 0 ? _a : null;
        if (editing) {
            setRows((prev) => prev.map((row) => row.key === editing.key ? Object.assign(Object.assign(Object.assign({}, row), fields), { techName: undefined, techRole: null, errors: undefined }) : row));
        }
        else {
            setRows((prev) => [...prev, Object.assign(Object.assign({}, fields), { key: nextKey(), primary: false, canDelete: true })]);
        }
        setEditor(null);
    };
    const saveNow = (values) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        const editing = (_a = editor === null || editor === void 0 ? void 0 : editor.row) !== null && _a !== void 0 ? _a : null;
        setBusy(true);
        setEditorError(null);
        try {
            if ((editing === null || editing === void 0 ? void 0 : editing.id) !== undefined) {
                yield updateVisit((_b = endpoints === null || endpoints === void 0 ? void 0 : endpoints.update) !== null && _b !== void 0 ? _b : '', editing.id, inspectionId !== null && inspectionId !== void 0 ? inspectionId : 0, values);
            }
            else {
                yield createVisit((_c = endpoints === null || endpoints === void 0 ? void 0 : endpoints.create) !== null && _c !== void 0 ? _c : '', inspectionId !== null && inspectionId !== void 0 ? inspectionId : 0, values, series !== null);
            }
            reloadPage();
        }
        catch (error) {
            setEditorError(serverErrorMessage(error, t('save_failed')));
            setBusy(false);
        }
    });
    const handleSubmit = (values) => {
        if (isImmediate)
            void saveNow(values);
        else
            stage(values);
    };
    const removeNow = (row) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        setBusy(true);
        try {
            yield destroyVisit((_a = endpoints === null || endpoints === void 0 ? void 0 : endpoints.destroy) !== null && _a !== void 0 ? _a : '', row.id);
            reloadPage();
        }
        catch (error) {
            setRemoveError(serverErrorMessage(error, t('remove_failed')));
            setBusy(false);
            setRemovingKey(null);
        }
    });
    const handleConfirmRemove = () => {
        const target = rows.find((row) => row.key === removingKey);
        if (!target)
            return;
        if (isImmediate) {
            setRemoveError(null);
            void removeNow(target);
            return;
        }
        setRows((prev) => prev.flatMap((row) => {
            if (row.key !== target.key)
                return [row];
            return row.id === undefined ? [] : [Object.assign(Object.assign({}, row), { destroyed: true })];
        }));
        setRemovingKey(null);
    };
    const visibleRows = rows.filter((row) => !row.destroyed);
    const rootClass = ['visits-section', !isImmediate && 'inspection-additional-visits'].filter(Boolean).join(' ');
    return (React.createElement("div", { className: rootClass },
        React.createElement(SectionHeader, { title: title !== null && title !== void 0 ? title : t('title'), actions: canEdit && React.createElement(Button, { onClick: openAdd }, t('add_visit')) }),
        removeError && React.createElement(InlineNotification, { variant: "error" }, removeError),
        visibleRows.length > 0 && (React.createElement(VisitsTable, { rows: visibleRows, subcontractorsEnabled: subcontractorsEnabled, showPriority: showPriority, canEdit: canEdit, assigneeName: assigneeName, onEdit: openEdit, onRemove: setRemovingKey })),
        !isImmediate && React.createElement(VisitHiddenInputs, { rows: rows }),
        editor && (React.createElement(VisitBrushaway, { initialDraft: editor.draft, visitId: (_a = editor.row) === null || _a === void 0 ? void 0 : _a.id, isEditing: editor.row !== null, defaults: defaults, subcontractorsEnabled: subcontractorsEnabled, technicianOptions: technicianOptions, subcontractorOptions: subcontractorOptions, timeZoneOptions: timeZoneOptions, checkAvailabilityUrl: (_b = endpoints === null || endpoints === void 0 ? void 0 : endpoints.checkAvailability) !== null && _b !== void 0 ? _b : '', series: editor.series, saving: busy, serverError: editorError, onSubmit: handleSubmit, onClose: closeEditor })),
        React.createElement(ConfirmDialog, { isOpen: removingKey !== null, title: t('remove_title'), description: isImmediate ? t('remove_description_immediate') : t('remove_description_form'), confirmLabel: tGeneric('remove'), confirmVariant: "danger", loading: busy, onConfirm: handleConfirmRemove, onCancel: () => {
                if (!busy)
                    setRemovingKey(null);
            } })));
}
export default VisitsSection;
